import React, { useState, useEffect } from 'react';
import { Plus, X, MessageCircle, Clock, CheckCircle, AlertCircle, Send } from 'lucide-react';

const API = 'https://talentflow-backend-production-09dd.up.railway.app/api';

const statusConfig = {
  open: { bg: '#fef9c3', color: '#ca8a04', label: 'Open' },
  'in-progress': { bg: '#dbeafe', color: '#2563eb', label: 'In Progress' },
  resolved: { bg: '#dcfce7', color: '#16a34a', label: 'Resolved' },
  closed: { bg: '#f1f5f9', color: '#64748b', label: 'Closed' },
};

const priorityConfig = {
  low: { bg: '#f1f5f9', color: '#64748b' },
  medium: { bg: '#fef9c3', color: '#ca8a04' },
  high: { bg: '#fee2e2', color: '#dc2626' },
  urgent: { bg: '#fce7f3', color: '#be185d' },
};

export default function SupportPage({ token, user }) {
  const [tickets, setTickets] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState('');
  const [form, setForm] = useState({ subject: '', message: '', category: 'general', priority: 'medium' });
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { loadTickets(); }, []);

  const loadTickets = async () => {
    try {
      const url = user.role === 'admin' ? `${API}/support` : `${API}/support/my`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (d.success) setTickets(d.tickets || []);
    } catch {}
  };

  const createTicket = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const r = await fetch(`${API}/support`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      const d = await r.json();
      if (d.success) {
        setMsg('Ticket created!');
        setShowForm(false);
        setForm({ subject: '', message: '', category: 'general', priority: 'medium' });
        loadTickets();
      }
    } catch {}
    setLoading(false);
  };

  const sendReply = async (ticketId) => {
    if (!reply.trim()) return;
    try {
      const r = await fetch(`${API}/support/${ticketId}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: reply })
      });
      const d = await r.json();
      if (d.success) {
        setReply('');
        setSelected(d.ticket);
        loadTickets();
      }
    } catch {}
  };

  const updateStatus = async (ticketId, status) => {
    try {
      await fetch(`${API}/support/${ticketId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status })
      });
      loadTickets();
      setSelected(prev => ({ ...prev, status }));
    } catch {}
  };

  const inp = { width: '100%', padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#1e293b', fontSize: '14px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };

  const timeAgo = (date) => {
    const diff = Math.floor((new Date() - new Date(date)) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 1fr' : '1fr', gap: '20px' }}>
      {/* Left - Tickets list */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ display: 'flex', gap: '12px' }}>
            {['all', 'open', 'in-progress', 'resolved'].map(s => (
              <span key={s} style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', background: '#f1f5f9', color: '#64748b', cursor: 'pointer' }}>
                {s === 'all' ? `All (${tickets.length})` : `${s} (${tickets.filter(t => t.status === s).length})`}
              </span>
            ))}
          </div>
          {user.role !== 'admin' && (
            <button onClick={() => setShowForm(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 18px', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', fontFamily: 'inherit' }}>
              <Plus size={14} /> New Ticket
            </button>
          )}
        </div>

        {msg && <div style={{ padding: '10px 16px', background: '#dcfce7', color: '#16a34a', borderRadius: '8px', marginBottom: '12px', fontSize: '13px' }}>{msg}</div>}

        {showForm && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '24px', border: '1px solid #e2e8f0', marginBottom: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
              <h3 style={{ color: '#1e293b', fontSize: '15px', fontWeight: '600', margin: 0 }}>New Support Ticket</h3>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={18} /></button>
            </div>
            <form onSubmit={createTicket}>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ color: '#64748b', fontSize: '11px', fontWeight: '600', display: 'block', marginBottom: '6px' }}>SUBJECT *</label>
                <input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} placeholder="Brief description of your issue" style={inp} required />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div>
                  <label style={{ color: '#64748b', fontSize: '11px', fontWeight: '600', display: 'block', marginBottom: '6px' }}>CATEGORY</label>
                  <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} style={{ ...inp, cursor: 'pointer' }}>
                    <option value="general">General</option>
                    <option value="technical">Technical</option>
                    <option value="billing">Billing</option>
                    <option value="bug">Bug Report</option>
                    <option value="feature">Feature Request</option>
                  </select>
                </div>
                <div>
                  <label style={{ color: '#64748b', fontSize: '11px', fontWeight: '600', display: 'block', marginBottom: '6px' }}>PRIORITY</label>
                  <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} style={{ ...inp, cursor: 'pointer' }}>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ color: '#64748b', fontSize: '11px', fontWeight: '600', display: 'block', marginBottom: '6px' }}>MESSAGE *</label>
                <textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder="Describe your issue in detail..." style={{ ...inp, height: '100px', resize: 'vertical' }} required />
              </div>
              <button type="submit" disabled={loading} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 20px', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', fontFamily: 'inherit' }}>
                <Send size={14} /> {loading ? 'Submitting...' : 'Submit Ticket'}
              </button>
            </form>
          </div>
        )}

        <div style={{ display: 'grid', gap: '10px' }}>
          {tickets.map(t => {
            const sc = statusConfig[t.status] || statusConfig.open;
            const pc = priorityConfig[t.priority] || priorityConfig.medium;
            return (
              <div key={t._id} onClick={() => setSelected(t)} style={{ background: 'white', borderRadius: '12px', padding: '16px', border: `1px solid ${selected?._id === t._id ? '#6366f1' : '#e2e8f0'}`, cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', transition: 'border 0.2s' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <h4 style={{ color: '#1e293b', fontSize: '14px', fontWeight: '600', margin: 0, flex: 1, marginRight: '12px' }}>{t.subject}</h4>
                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                    <span style={{ padding: '3px 8px', background: pc.bg, color: pc.color, borderRadius: '4px', fontSize: '10px', fontWeight: '600' }}>{t.priority?.toUpperCase()}</span>
                    <span style={{ padding: '3px 8px', background: sc.bg, color: sc.color, borderRadius: '4px', fontSize: '10px', fontWeight: '600' }}>{sc.label}</span>
                  </div>
                </div>
                <p style={{ color: '#64748b', fontSize: '13px', margin: '0 0 8px', lineHeight: '1.5' }}>{t.message?.slice(0, 80)}...</p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <span style={{ color: '#94a3b8', fontSize: '11px' }}>{t.category}</span>
                    {user.role === 'admin' && t.user && <span style={{ color: '#94a3b8', fontSize: '11px' }}>by {t.user.name}</span>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {t.replies?.length > 0 && <span style={{ color: '#6366f1', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '3px' }}><MessageCircle size={11} /> {t.replies.length}</span>}
                    <span style={{ color: '#94a3b8', fontSize: '11px' }}>{timeAgo(t.createdAt)}</span>
                  </div>
                </div>
              </div>
            );
          })}
          {tickets.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px', background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', color: '#94a3b8' }}>
              <MessageCircle size={40} color="#e2e8f0" style={{ margin: '0 auto 12px', display: 'block' }} />
              <p style={{ margin: 0 }}>{user.role === 'admin' ? 'No support tickets yet' : 'No tickets raised yet'}</p>
            </div>
          )}
        </div>
      </div>

      {/* Right - Ticket detail */}
      {selected && (
        <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', height: 'fit-content', position: 'sticky', top: '80px' }}>
          <div style={{ padding: '20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <h3 style={{ color: '#1e293b', fontSize: '15px', fontWeight: '600', margin: '0 0 8px' }}>{selected.subject}</h3>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ padding: '3px 8px', background: statusConfig[selected.status]?.bg, color: statusConfig[selected.status]?.color, borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>{statusConfig[selected.status]?.label}</span>
                <span style={{ padding: '3px 8px', background: '#f1f5f9', color: '#64748b', borderRadius: '4px', fontSize: '11px' }}>{selected.category}</span>
              </div>
            </div>
            <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={18} /></button>
          </div>

          {user.role === 'admin' && (
            <div style={{ padding: '12px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: '8px' }}>
              {['open', 'in-progress', 'resolved', 'closed'].map(s => (
                <button key={s} onClick={() => updateStatus(selected._id, s)} style={{ padding: '4px 10px', background: selected.status === s ? '#6366f1' : '#f1f5f9', color: selected.status === s ? 'white' : '#64748b', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '600', fontFamily: 'inherit' }}>
                  {s}
                </button>
              ))}
            </div>
          )}

          <div style={{ padding: '20px', maxHeight: '400px', overflowY: 'auto' }}>
            <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
              <p style={{ color: '#1e293b', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>{selected.message}</p>
            </div>
            {(selected.replies || []).map((r, i) => (
              <div key={i} style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <span style={{ color: r.isAdmin ? '#6366f1' : '#64748b', fontSize: '12px', fontWeight: '600' }}>{r.isAdmin ? '👨‍💼 Admin' : '👤 You'}</span>
                  <span style={{ color: '#94a3b8', fontSize: '11px' }}>{timeAgo(r.createdAt)}</span>
                </div>
                <div style={{ background: r.isAdmin ? '#ede9fe' : '#f8fafc', borderRadius: '8px', padding: '12px' }}>
                  <p style={{ color: '#1e293b', fontSize: '14px', lineHeight: '1.5', margin: 0 }}>{r.message}</p>
                </div>
              </div>
            ))}
          </div>

          {selected.status !== 'closed' && (
            <div style={{ padding: '16px 20px', borderTop: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <textarea value={reply} onChange={e => setReply(e.target.value)} placeholder="Type your reply..." style={{ flex: 1, padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#1e293b', fontSize: '14px', outline: 'none', resize: 'none', height: '60px', fontFamily: 'inherit' }} />
                <button onClick={() => sendReply(selected._id)} style={{ padding: '10px 16px', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit' }}>
                  <Send size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
